import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, BookOpen, User, Settings, HelpCircle } from 'lucide-react';
import ArvexisLogo from './ArvexisLogo';

// Dreams V1 desktop shell. A responsive adaptation of the existing app shell for the Dreams
// surfaces only — the routes, global navigation destinations and mobile bottom nav are unchanged.
// Below 1024px this renders nothing but its children.

// The approved desktop sidebar, limited to destinations that already exist:
// Home, Dream Journal — divider — Profile, Settings, Help.
// `Explore` and `Saved` are NOT here: they have no destination in the app and a visual reference
// is not authorization to create one (Founder ruling, 2026-09-19).
const ITEMS = [
  { icon: Home, id: 'home', label: { en: 'Home', es: 'Inicio' }, path: '/home' },
  { icon: BookOpen, id: 'dream-journal', label: { en: 'Dream Journal', es: 'Diario de Sueños' }, path: '/dreams' },
  { divider: true, id: 'divider' },
  { icon: User, id: 'profile', label: { en: 'Profile', es: 'Perfil' }, path: '/profile' },
  { icon: Settings, id: 'settings', label: { en: 'Settings', es: 'Ajustes' }, path: '/settings' },
  { icon: HelpCircle, id: 'help', label: { en: 'Help', es: 'Ayuda' }, path: '/help-center' },
];

// One canonical desktop background, shared by every desktop screen in this shell.
const CANONICAL_DESKTOP_BACKGROUND = `
  linear-gradient(180deg, rgba(4,6,11,0.30) 0%, rgba(4,6,11,0.55) 100%),
  url('/Canonical_Desktop_Background_v2.png') center center / cover no-repeat,
  linear-gradient(180deg, #070A12 0%, #04060B 100%)
`;

const FOOTER_WORDS = {
  en: ['REFLECT', 'EXPLORE', 'UNDERSTAND', 'GROW'],
  es: ['REFLEXIONA', 'EXPLORA', 'COMPRENDE', 'CRECE'],
};

// `activePath` lets an account surface declare which existing sidebar destination is active
// (Change Password reads as Settings). It adds no destination and changes no route.
export const DreamsShell = ({ children, lang = 'en', activePath }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(mq.matches);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const isActive = (path) =>
    activePath
      ? path === activePath
      : path === '/dreams'
      ? ['/dreams', '/journal', '/log', '/reflection', '/interpretation'].some((p) =>
          location.pathname.startsWith(p)
        )
      : location.pathname.startsWith(path);

  return (
    <>
      {/* The phone navigation bar is not a desktop composition */}
      <style>{`@media (min-width: 1024px){ .bottom-nav{display:none !important;} }`}</style>

      {isDesktop && (
        <aside
          className="fixed left-0 top-0 bottom-0 z-40 flex flex-col"
          style={{
            width: 205,
            background: 'linear-gradient(180deg,#0A0E18 0%,#070A12 100%)',
            borderRight: '1px solid rgba(255,255,255,0.05)',
          }}
          data-testid="dreams-sidebar"
        >
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="flex flex-col items-center gap-2 pt-8 pb-9"
            data-testid="sidebar-logo"
          >
            <ArvexisLogo size={44} />
            <span
              className="text-white/85 text-[13px]"
              style={{ letterSpacing: '0.32em', fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              ARVEXIS
            </span>
            <span
              className="text-[9px]"
              style={{
                letterSpacing: '0.34em',
                color: 'rgba(230,192,110,0.7)',
                fontFamily: "'Inter', system-ui, sans-serif",
              }}
            >
              {lang === 'es' ? 'UN TÚ MÁS PROFUNDO' : 'A DEEPER YOU'}
            </span>
          </button>

          <nav className="px-3 space-y-1">
            {ITEMS.map((item) => {
              if (item.divider) {
                return (
                  <div
                    key={item.id}
                    className="mx-4 my-4"
                    style={{ height: 1, background: 'rgba(255,255,255,0.08)' }}
                    data-testid="sidebar-divider"
                  />
                );
              }
              const { icon: Icon, id, label, path } = item;
              const active = !!path && isActive(path);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { if (path) navigate(path); }}
                  className="w-full flex items-center gap-2.5 px-3 py-3 rounded-xl transition-colors"
                  style={
                    active
                      ? {
                          background: 'rgba(230,192,110,0.08)',
                          border: '1px solid rgba(230,192,110,0.28)',
                          color: '#E6C06E',
                        }
                      : { color: 'rgba(255,255,255,0.62)' }
                  }
                  data-testid={`sidebar-${id}`}
                >
                  <Icon className="w-[18px] h-[18px]" />
                  <span className="text-sm whitespace-nowrap">{label[lang === 'es' ? 'es' : 'en']}</span>
                  {active && (
                    <span
                      className="ml-auto rounded-full"
                      style={{ width: 6, height: 6, background: '#E6C06E' }}
                    />
                  )}
                </button>
              );
            })}
          </nav>

          <div className="mt-auto px-7 pb-8" data-testid="sidebar-footer-words">
            {FOOTER_WORDS[lang === 'es' ? 'es' : 'en'].map((word) => (
              <p
                key={word}
                className="text-[11px] leading-[1.7]"
                style={{ letterSpacing: '0.3em', color: 'rgba(255,255,255,0.28)' }}
              >
                {word}
              </p>
            ))}
          </div>
        </aside>
      )}

      {/* One canonical background for every desktop screen in this shell */}
      {isDesktop && (
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          style={{ background: CANONICAL_DESKTOP_BACKGROUND }}
          data-testid="canonical-desktop-background"
        />
      )}

      <div
        className={isDesktop ? 'relative z-10' : undefined}
        style={isDesktop ? { paddingLeft: 205 } : undefined}
      >
        {children}
      </div>
    </>
  );
};

export default DreamsShell;
